import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import { pgParksData } from './mockParksData';
import "./findParksNearMe.css"

// Fix leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png'
});

const getDistanceInMiles = (lat1, log1, lat2, log2) => {
    const R = 6371; // km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((log2 - log1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const disKm = R * c;
    return disKm * 0.621371;
}

const userIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

const FindParksNearMe = () => {
    const [userLocation, setUserLocation] = useState(null);
    const [allNearbyParks, setAllNearbyParks] = useState([]);
    const [nearbyParks, setNearbyParks] = useState([]);
    const [showAll, setShowAll] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const markerRefs = useRef({});
    const userMarkerRef = useRef(null);

    const location = useLocation();
    const navigate = useNavigate();


    const handleGetLocation = () => {
        if (!navigator.geolocation) {
            setError("Geolocation is not supported by your browser.");
            return;
        }
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
                setError('');
                setLoading(false);
            },
            (err) => {
                console.error("Geolocation Error:", err);
                setError("Unable to retrieve your location.");
                setLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        );

    }

    useEffect(() => {
        const { auto } = queryString.parse(location.search);
        if (auto && !userLocation) {
            handleGetLocation();
            return;
        }
        if (!userLocation) return;

        const parksWithDistance = pgParksData.map((park) => ({
            ...park,
            distance: getDistanceInMiles(userLocation.lat, userLocation.lng, park.lat, park.lng),
        }));

        const sortedParks = parksWithDistance.sort((a, b) => a.distance - b.distance);

        // Top 3 closest parks
        const top3Parks = sortedParks.slice(0, 3);

        // Next 7 closest parks
        const next7Parks = sortedParks.slice(3, 10);

        setAllNearbyParks([...top3Parks, ...next7Parks]);
        setNearbyParks(top3Parks);
        setLoading(false);
    }, [location.search, userLocation]);


    const handleShowAll = () => {
        setNearbyParks(allNearbyParks);
        setShowAll(true);
    }

    const getGoogleMapsUrl = (lat, lng) => {
        return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    };

    return (
        <main id="main-content">
        <div className="container py-5">
            {/* Back Button */}
            <button className="btn btn-outline-secondary mb-4" onClick={() => navigate(-1)}>
                ← Back
            </button>
            <div className="row">
                {/* Nearby list */}
                <div className="col-md-5">
                    {/* {!userLocation && (<button onClick={handleGetLocation}>📍 Use My Location</button>)} */}
                    {loading && <p>⏳ Looking for parks near your location...</p>}
                    {error && <p style={{ color: 'red' }}>{error}</p>}
                    {
                        nearbyParks.length > 0 && !loading && (
                            <div>
                                <h1 className="d-flex align-items-center gap-2" 
                                style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "1rem"}}>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/image/ParksIcon.png`}
                                        alt="Park Icon"
                                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                    />
                                    {showAll ? 'Nearby Fun (Top 10)' : 'Nearby Fun (Top 3)'}

                                    <img
                                        src={`${process.env.PUBLIC_URL}/image/ParkIcon.png`}
                                        alt="Park Icon"
                                        style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                    />
                                </h1>
                                <hr />

                                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                                    {nearbyParks.map(park => (
                                        <li
                                            key={park.id}
                                            style={{ marginBottom: '0.75rem' }}
                                            onMouseEnter={() => {
                                                const marker = markerRefs.current[park.id];
                                                if (marker) marker.openPopup();
                                            }}
                                            onMouseLeave={() => {
                                                const marker = markerRefs.current[park.id];
                                                if (marker) marker.closePopup();
                                            }}
                                        >

                                            <a
                                                href={getGoogleMapsUrl(park.lat, park.lng)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ fontWeight: 'bold', color: '#0077cc', textDecoration: 'none' }}
                                            >
                                                {park.name}
                                            </a>
                                            <br />
                                            <small>{park.category} — {park.distance.toFixed(1)} miles away</small>
                                        </li>
                                    ))}
                                </ul>
                                {!showAll && allNearbyParks.length > 3 && (
                                    <button onClick={handleShowAll}>➕ More Locations</button>
                                )}
                            </div>
                        )
                    }

                </div>
                {/* Map section */}
                <div className="col-md-7" style={{ height: 'calc(100vh - 200px)' }}>

                    {userLocation && (
                        <MapContainer
                            center={[userLocation.lat, userLocation.lng]}
                            zoom={13}
                            scrollWheelZoom={false}
                            style={{ height: "100%", width: "100%" }}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />

                            {/* User Marker */}
                            <Marker
                                position={[userLocation.lat, userLocation.lng]}
                                icon={userIcon}
                                ref={userMarkerRef}
                                eventHandlers={{
                                    mouseover: () => {
                                        if (userMarkerRef.current) {
                                            userMarkerRef.current.openPopup();
                                        }
                                    },
                                    mouseout: () => {
                                        if (userMarkerRef.current) {
                                            userMarkerRef.current.closePopup();
                                        }
                                    }
                                }}
                            >
                                <Popup>Your Location</Popup>
                            </Marker>



                            {/* Parks Markers */}
                            {nearbyParks.map(park => (
                                <Marker
                                    key={park.id}
                                    position={[park.lat, park.lng]}
                                    icon={defaultIcon}
                                    ref={ref => {
                                        if (ref) {
                                            markerRefs.current[park.id] = ref;
                                        }
                                    }}
                                    eventHandlers={{
                                        mouseover: (e) => {
                                            if (markerRefs.current[park.id]) {
                                                markerRefs.current[park.id].openPopup();
                                            }
                                        },
                                        mouseout: (e) => {
                                            if (markerRefs.current[park.id]) {
                                                markerRefs.current[park.id].closePopup();
                                            }
                                        }
                                    }}
                                >
                                    <Popup>
                                        <strong>{park.name}</strong><br />
                                        {park.category}<br />
                                        {park.distance.toFixed(1)} miles away
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}
                </div>
            </div>
        </div>
        </main>
    );
}

export default FindParksNearMe;



